import { z } from 'zod';

import { hexColorSchema, idSchema, isoDateSchema } from './common.schema';

export const widgetModeSchema = z.enum(['popup', 'inline']);
export const widgetTypeSchema = z.enum(['chat', 'form', 'survey', 'promo']);

// ─── Behavior triggers (client-side, GDD §2.3.1) ──────────────────────────
export const triggerTypeSchema = z.enum([
  'exit_intent',
  'scroll',
  'time_on_page',
  'url_match',
  'idle',
  'js_event',
  'visit_count',
  'device',
  'utm',
]);

export const triggerRuleSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    id: z.string(),
    enabled: z.boolean().default(true),
    conditions: z.union([
      z.object({
        logic: z.enum(['AND', 'OR']),
        rules: z.array(z.union([z.lazy(() => triggerRuleSchema), z.record(z.unknown())])),
      }),
      z.record(z.unknown()),
    ]),
    actions: z.array(z.record(z.unknown())),
    on_interaction: z.record(z.unknown()).optional(),
  }),
);

// ─── Form fields ───────────────────────────────────────────────────────────
export const formFieldTypeSchema = z.enum([
  'text',
  'email',
  'phone',
  'textarea',
  'select',
  'radio',
  'checkbox',
  'rating',
  'file',
  'hidden',
]);

export const formFieldSchema = z.object({
  id: z.string(),
  type: formFieldTypeSchema,
  label: z.string(),
  placeholder: z.string().optional(),
  required: z.boolean().optional(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  hint: z.string().optional(),
  default: z.unknown().optional(),
  /** Условная видимость поля */
  visible_if: z
    .object({
      logic: z.enum(['AND', 'OR']).default('AND'),
      rules: z.array(z.object({ field: z.string(), op: z.string(), value: z.unknown() })),
    })
    .optional(),
  /** Валидация */
  min: z.number().optional(),
  max: z.number().optional(),
  pattern: z.string().optional(),
});
export type FormField = z.infer<typeof formFieldSchema>;

export const formStepSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  fields: z.array(formFieldSchema),
  routing: z
    .object({
      field: z.string(),
      rules: z.array(z.object({ op: z.string(), value: z.unknown(), next: z.string() })),
      default: z.string().optional(),
    })
    .optional(),
  submit_button: z
    .object({
      label: z.string(),
      action: z.enum(['create_ticket', 'next_step', 'open_chat']).default('create_ticket'),
    })
    .optional(),
});

// ─── Widget design config ──────────────────────────────────────────────────
export const widgetDesignConfigSchema = z
  .object({
    type: widgetTypeSchema.default('chat'),
    mode: widgetModeSchema.default('popup'),
    position: z
      .enum(['bottom-right', 'bottom-left', 'top-right', 'top-left'])
      .default('bottom-right'),
    accent_color: hexColorSchema.default('#7F77DD'),
    accent_text_color: hexColorSchema.optional(),
    bg_color: hexColorSchema.optional(),
    border_radius: z.string().default('6px'),
    font: z.string().optional(),
    font_size: z.string().optional(),
    z_index: z.number().int().optional(),
    /** Шапка */
    header: z
      .object({
        title: z.string().optional(),
        subtitle: z.string().optional(),
        logo_url: z.string().url().optional(),
        hidden: z.boolean().optional(),
      })
      .optional(),
    /** Шаги формы */
    steps: z.array(formStepSchema).optional(),
    /** Кастомный CSS URL */
    custom_css_url: z.string().url().optional(),
    /** Согласие на ПД (GDPR-чекбокс) */
    consent: z
      .object({
        enabled: z.boolean().default(false),
        label: z.string().optional(),
        required: z.boolean().default(true),
        policy_url: z.string().url().optional(),
        policy_version: z.string().optional(),
      })
      .optional(),
  })
  // designConfig — гибкий JSON, форма зависит от типа виджета (chat/form/survey:
  // fields/questions/welcome_message и т.п.). Без passthrough zod вырезает все
  // поля редакторов → «настройки не сохраняются».
  .passthrough();

// ─── Widget root schema ───────────────────────────────────────────────────
export const widgetSchema = z.object({
  id: idSchema,
  workspace_id: idSchema,
  public_key: z.string().min(8).max(64),
  name: z.string().min(1).max(200),
  type: widgetTypeSchema,
  mode: widgetModeSchema,
  design_config: widgetDesignConfigSchema,
  trigger_rules: z.array(z.unknown()).default([]),
  bot_scenario_id: idSchema.nullable().optional(),
  custom_css_url: z.string().url().nullable().optional(),
  default_group_id: idSchema.nullable().optional(),
  allowed_domains: z.array(z.string()).default([]),
  is_active: z.boolean(),
  created_at: isoDateSchema,
  updated_at: isoDateSchema,
});
export type Widget = z.infer<typeof widgetSchema>;

export const createWidgetSchema = z.object({
  name: z.string().min(1).max(200),
  /** Магазин, на сайте которого стоит виджет (диалоги получают его магазин) */
  store_id: idSchema.nullable().optional(),
  type: widgetTypeSchema.default('chat'),
  mode: widgetModeSchema.default('popup'),
  /** v1 поле — оставлено для обратной совместимости. Новый код пишет в четыре
   *  раздела ниже (split design_config → theme/content/behavior/media). */
  design_config: widgetDesignConfigSchema.optional(),
  theme_config: z.record(z.unknown()).optional(),
  content_config: z.record(z.unknown()).optional(),
  behavior_config: z.record(z.unknown()).optional(),
  media_config: z.record(z.unknown()).optional(),
  trigger_rules: z.array(z.unknown()).optional(),
  bot_scenario_id: idSchema.optional(),
  default_group_id: idSchema.optional(),
  allowed_domains: z.array(z.string()).optional(),
  custom_css_url: z.string().url().nullable().optional(),
  is_active: z.boolean().optional(),
});

export const updateWidgetDesignSchema = widgetDesignConfigSchema.partial();
export const updateWidgetSchema = createWidgetSchema.partial();
